<template>
  <footer id="discussionFooter" class="community-post-footer">
    <div class="post-actions">
      <icon-button
        type="button"
        icon="thumb_up"
        :icons-outlined="!likeId"
        :reduce="false"
        :submitting="makingApiCall"
        submitting-label="Saving..."
        classes="-ico-txt -no-case -no-border like-action"
        :label="`${likeCount} Like`"
        aria-live="polite"
        @click-action="toggleLike"
      />
    </div>

    <div class="post-actions">
      <discussion-actions-menu v-if="isOwner" :discussion-id="discussionId" />

      <div v-if="!isOwner" class="flag" :class="{ '-flagged': isFlagged }">
        <icon-button
          icon="flag"
          classes="-ico-only -no-container"
          aria-label="Flag"
          :icons-outlined="!isFlagged"
          @click-action="openFlagModal"
        />
      </div>

      <pay-forward-action-menu
        v-if="!isOwner && showPayForward"
        entity-type="discussion"
        :entity-id="discussionId"
        :gift-user-id="authorId"
      />
    </div>
  </footer>
</template>

<script setup lang="ts">
import { computed, ref, watch } from 'vue'
import { useUserAuthStore } from '@/stores/user-auth'
import { useModalsStore } from '@/stores/modals'
import { fbApi } from '@/utils/http-api'
import IconButton from '@/components/buttons/icon-button.vue'
import DiscussionActionsMenu from '@/components/discussions/discussion-actions-menu.vue'
import PayForwardActionMenu from '@/components/cart/pay-forward-action-menu.vue'

/** PROPS */
const props = withDefaults(
  defineProps<{
    authorId: number
    discussionId: number
    showPayForward: boolean
    initLikeId?: number
    initLikeCount?: number
    initIsFlagged?: boolean
  }>(),
  {
    initLikeId: 0,
    initLikeCount: 0,
    initIsFlagged: false,
  },
)

/** NON-REACTIVE */
const CONTENT_TYPE = 'discussion'

/** STATE */
const userAuthStore = useUserAuthStore()
const modalsStore = useModalsStore()

const makingApiCall = ref(false)
const likeId = ref<number>(props.initLikeId)
const likeCount = ref<number>(props.initLikeCount)
const isFlagged = ref<boolean>(props.initIsFlagged)

/** COMPUTED */
const isOwner = computed(() => {
  return userAuthStore.user.id === props.authorId
})

const flagModalId = computed(() => `flag-discussion-${props.discussionId}-modal`)

/** WATCHERS **/
watch(
  () => modalsStore.simpleModal.action,
  (newAction: string, prevAction: string) => {
    if (
      modalsStore.simpleModal.id === flagModalId.value &&
      newAction !== prevAction &&
      newAction === 'flagged'
    ) {
      isFlagged.value = true
    }
  },
)

/** METHODS */

async function toggleLike() {
  if (!userAuthStore.user.isLoggedIn) {
    modalsStore.showLoginModal()
    return
  }

  makingApiCall.value = true

  if (!likeId.value) {
    // post
    try {
      // instantly increment count
      likeCount.value += 1

      const res = await fbApi.post('/like', {
        json: {
          likeable_type: CONTENT_TYPE,
          likeable_id: props.discussionId,
          //TODO: CI - Comment Improvement Feature - Since likes now support a react type
          // passing in 1 for discussion likes as the default 👍.
          react_type_id: 1,
        },
      })

      if (!res || !res.body || !res.body.data) {
        new Error(`Unable to update like, please try again`)
      }

      likeId.value = res.body.data.id
    } catch {
      // reset like count
      likeCount.value -= 1
    }
  } else {
    // delete
    try {
      // decrement count
      likeCount.value = likeCount.value > 0 ? likeCount.value - 1 : 0

      const res = await fbApi.delete(`/like/${likeId.value}`, {
        json: {
          likeable_type: CONTENT_TYPE,
          likeable_id: props.discussionId,
          //TODO: CI - Comment Improvement Feature - Since likes now support a react type
          // passing in 1 for discussion likes as the default 👍.
          react_type_id: 1,
        },
      })

      if (!res || !res.body || !res.body.data) {
        new Error(`Unable to update like, please try again`)
      }

      // clear out
      likeId.value = 0
    } catch {
      // set like count
      likeCount.value += 1
    }
  }

  makingApiCall.value = false
}

function openFlagModal() {
  modalsStore.openSimpleModal({
    type: 'FLAG',
    id: flagModalId.value,
    props: {
      flaggableId: props.discussionId,
      flaggableType: CONTENT_TYPE,
    },
  })
}
</script>
